/**
 * Archive and listing filters.
 *
 * Upgrades every filter form on the page so choosing a value updates its own
 * results in place. Everything here is an enhancement: each form is a real GET
 * form pointed at the page it sits on, so without JavaScript the dropdowns,
 * the search box and the remove-filter chips (plain links) still work through
 * an ordinary page load.
 *
 * A form drives the results element named by its data-archive-target, which
 * lets the archive templates put the bar in the page header and the results
 * further down, and lets several Post Listing blocks share a page without
 * driving each other.
 *
 * On small screens the dropdowns and the search box fold behind a "Filter By"
 * disclosure button and a search button (aria-expanded kept in step).
 *
 * On a built archive, filtering replaces the curated blocks with a flat grid of
 * matches; clearing every filter puts the blocks back.
 */
(function () {
    'use strict';

    var forms = document.querySelectorAll('[data-archive-filters]');

    if (!forms.length) {
        return;
    }

    Array.prototype.forEach.call(forms, function (form) {
        setupDisclosures(form);

        // A bar can opt out of in-place filtering (filter-bar.php 'ajax' => false),
        // e.g. where the results aren't posts the AJAX endpoint can return.
        if (typeof tdwd_ajax === 'undefined' || form.dataset.archiveAjax === 'false') {
            return;
        }

        var region = document.getElementById(form.dataset.archiveTarget || 'archive-results');

        if (!region) {
            return;
        }

        setup(form, region);
    });

    /**
     * The small-screen "Filter By" and search buttons. Each one shows and hides
     * the panel named by its aria-controls; the stylesheet only folds the
     * panels away once the form carries archive-filters--enhanced, so nothing
     * is hidden if this never runs.
     *
     * @param {HTMLFormElement} form The filter form.
     */
    function setupDisclosures(form) {
        var toggles = form.querySelectorAll('[data-archive-toggle], [data-archive-search-toggle]');

        Array.prototype.forEach.call(toggles, function (toggle) {
            var panel = document.getElementById(toggle.getAttribute('aria-controls'));

            if (!panel) {
                return;
            }

            toggle.hidden = false;

            // A search already in force starts open, so the reader can see it.
            var input = panel.querySelector('input');
            var startOpen = !!(input && input.value);

            setOpen(toggle, panel, startOpen);

            toggle.addEventListener('click', function () {
                var open = toggle.getAttribute('aria-expanded') !== 'true';

                setOpen(toggle, panel, open);

                if (open) {
                    var first = panel.querySelector('select, input');

                    if (first) {
                        first.focus();
                    }
                }
            });

            // Escape closes the panel and puts focus back on its button.
            panel.addEventListener('keydown', function (event) {
                if (event.key === 'Escape' && toggle.offsetParent !== null) {
                    setOpen(toggle, panel, false);
                    toggle.focus();
                }
            });
        });

        form.classList.add('archive-filters--enhanced');
    }

    /**
     * @param {HTMLElement} toggle The disclosure button.
     * @param {HTMLElement} panel  The panel it controls.
     * @param {boolean}     open   Whether to show the panel.
     */
    function setOpen(toggle, panel, open) {
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        panel.classList.toggle('is-open', open);
    }

    /**
     * Wire one filter form to the results it drives.
     *
     * @param {HTMLFormElement} form   The filter form.
     * @param {HTMLElement}     region The results element it updates.
     */
    function setup(form, region) {
        var archive = region.dataset.archive || '';
        var nonce = region.dataset.archiveNonce || tdwd_ajax.archive_nonce;
        var isBlocks = region.classList.contains('archive-page__blocks');
        var blocksHtml = isBlocks ? region.innerHTML : null;
        var resultsClass = isBlocks ? 'archive-page__results' : region.className;
        var searchInput = form.querySelector('[data-archive-search]');
        var submit = form.querySelector('.archive-filters__submit');
        var chipList = form.querySelector('[data-archive-chips]');
        var busy = false;
        var debounce = null;

        // With the script running, the form applies itself on change.
        if (submit) {
            submit.hidden = true;
        }

        function currentParams() {
            var params = new URLSearchParams();

            form.querySelectorAll('[data-archive-filter]').forEach(function (select) {
                if (select.value) {
                    params.set(select.name, select.value);
                }
            });

            if (searchInput && searchInput.value.trim()) {
                params.set(searchInput.name, searchInput.value.trim());
            }

            return params;
        }

        function urlFor(params) {
            var query = params.toString();

            return query ? form.action + '?' + query : form.action;
        }

        function setBusy(value) {
            busy = value;
            region.setAttribute('aria-busy', value ? 'true' : 'false');
            region.classList.toggle('archive-page__results--busy', value);
            region.classList.toggle('post-listing__results--busy', value);
        }

        function updateAddressBar(params) {
            if (!window.history || !window.history.replaceState) {
                return;
            }

            window.history.replaceState({}, '', urlFor(params));
        }

        /**
         * Rebuild the remove-filter chips from the form's current values. Each
         * chip stays a real link to the page without that filter.
         */
        function renderChips() {
            if (!chipList) {
                return;
            }

            var params = currentParams();
            var chips = [];

            form.querySelectorAll('[data-archive-filter]').forEach(function (select) {
                if (select.value && select.selectedIndex > -1) {
                    chips.push({
                        name: select.name,
                        label: select.options[select.selectedIndex].text.trim()
                    });
                }
            });

            if (searchInput && searchInput.value.trim()) {
                chips.push({
                    name: searchInput.name,
                    label: '“' + searchInput.value.trim() + '”'
                });
            }

            chipList.innerHTML = '';

            chips.forEach(function (chip) {
                var without = new URLSearchParams(params.toString());
                without.delete(chip.name);

                var item = document.createElement('li');
                var link = document.createElement('a');
                var label = document.createElement('span');

                link.className = 'archive-filters__chip';
                link.href = urlFor(without);
                link.setAttribute('data-archive-chip', chip.name);
                link.setAttribute('aria-label', (tdwd_ajax.strings && tdwd_ajax.strings.removeFilter
                    ? tdwd_ajax.strings.removeFilter
                    : 'Remove filter: %s').replace('%s', chip.label));

                label.className = 'archive-filters__chip-label';
                label.textContent = chip.label;

                link.appendChild(label);
                link.insertAdjacentHTML('beforeend', '<svg class="archive-filters__chip-icon" width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true" focusable="false"><path d="M1 1L7 7M7 1L1 7" stroke="currentColor" stroke-linecap="round"/></svg>');
                item.appendChild(link);
                chipList.appendChild(item);
            });

            chipList.hidden = chips.length === 0;
        }

        /**
         * Put a built archive back to the layout it was saved with.
         */
        function restore(params) {
            if (blocksHtml === null) {
                // A listing has no saved markup to restore, so it reloads.
                window.location.href = urlFor(params);
                return;
            }

            region.className = 'archive-page__blocks';
            region.innerHTML = blocksHtml;
            region.removeAttribute('aria-live');
            updateAddressBar(new URLSearchParams());
        }

        function apply(params) {
            if (busy) {
                return;
            }

            renderChips();

            if (!params.toString() && isBlocks) {
                restore(params);
                return;
            }

            setBusy(true);

            var url = new URL(tdwd_ajax.ajaxurl, window.location.origin);

            url.searchParams.set('action', 'tdwd_archive_filter');
            url.searchParams.set('nonce', nonce);
            url.searchParams.set('archive', archive);
            url.searchParams.set('paged', '1');

            params.forEach(function (value, key) {
                url.searchParams.set(key, value);
            });

            fetch(url.toString(), { credentials: 'same-origin' })
                .then(function (response) {
                    return response.json();
                })
                .then(function (payload) {
                    if (!payload || !payload.success) {
                        throw new Error('failed');
                    }

                    render(payload.data, params);
                })
                .catch(function () {
                    // Fall back to a real page load rather than leaving a dead page.
                    window.location.href = urlFor(params);
                })
                .finally(function () {
                    setBusy(false);
                });
        }

        function render(data, params) {
            region.className = resultsClass;
            region.setAttribute('aria-live', 'polite');

            var count = data.total === 1
                ? tdwd_ajax.strings.oneResult
                : tdwd_ajax.strings.results.replace('%d', data.total);

            var countHtml = params.toString()
                ? '<p class="archive-page__count">' + count + '</p>'
                : '';

            region.innerHTML = countHtml + data.html;

            // The server-rendered page links belong to the unfiltered set, so
            // they would page through the wrong results.
            var pager = region.parentNode && region.parentNode.querySelector('.c-pagination');

            if (pager) {
                pager.hidden = !!params.toString() || !data.hasMore;
            }

            updateAddressBar(params);
        }

        form.addEventListener('submit', function (event) {
            event.preventDefault();
            apply(currentParams());
        });

        form.querySelectorAll('[data-archive-filter]').forEach(function (select) {
            select.addEventListener('change', function () {
                apply(currentParams());
            });
        });

        if (searchInput) {
            searchInput.addEventListener('input', function () {
                window.clearTimeout(debounce);
                debounce = window.setTimeout(function () {
                    apply(currentParams());
                }, 350);
            });
        }

        // Removing a chip clears its control and re-applies, then moves focus
        // to the next chip (or the bar) so it is not lost with the chip.
        if (chipList) {
            chipList.addEventListener('click', function (event) {
                var chip = event.target.closest('[data-archive-chip]');

                if (!chip) {
                    return;
                }

                event.preventDefault();

                var chips = Array.prototype.slice.call(chipList.querySelectorAll('[data-archive-chip]'));
                var index = chips.indexOf(chip);
                var name = chip.getAttribute('data-archive-chip');
                var control = form.querySelector('[name="' + name + '"]');

                if (control) {
                    control.value = '';
                }

                apply(currentParams());

                var remaining = chipList.querySelectorAll('[data-archive-chip]');
                var next = remaining[Math.min(index, remaining.length - 1)];

                if (next) {
                    next.focus();
                } else {
                    var fallback = form.querySelector('[data-archive-toggle]:not([hidden])');

                    if (!fallback || fallback.offsetParent === null) {
                        fallback = form.querySelector('[data-archive-filter], [data-archive-search]');
                    }

                    if (fallback) {
                        fallback.focus();
                    }
                }
            });
        }
    }
})();
